import { ACTION_STATUSES } from 'shared/consts';
import {
  bookingAddOnsEligibility,
  bookingAddOnsPrerequisites,
  hasOrderedAddOns,
} from 'store/booking-add-ons/eligibility';
import { bookingAddOnsMenuKey, selectBookingAddOnsMenu } from 'store/booking-add-ons/selectors';
import { getBookingAddOnsMenu } from 'store/booking-add-ons/actions';
import { useAddOnsEnabled } from 'hooks/use-add-ons-enabled.hook';
import { useCurrentAdapterName } from 'hooks/use-current-adapter-name';
import { useOwnerParams } from 'hooks/use-owner-params.hook';
import useKioskEnabled from 'store/kiosk/hooks/use-kiosk-enabled';
import { TransactionDetails } from 'store/transactions/types';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useMemo, useRef, useState } from 'react';

export const BOOKING_ADD_ONS_SEGMENT = 'add-on-request';

/* Not `bookingId`: `useUpcomingBookings` auto-opens a receipt wherever it sees that param. */
export const BOOKING_ADD_ONS_PARAM = 'addOnBookingId';

/*
 * Set when the tenant has answered the add-on offer with "no", so the receipt they land on shows the
 * receipt itself rather than offering again.
 *
 * ⛔ IT HAS TO TRAVEL IN THE URL. Declining leaves this step, which unmounts it and the receipt both,
 * so there is no component left holding the answer — and the booking is still eligible (nothing was
 * ordered), so the offer would re-qualify and the tenant would be asked the same question again by
 * the screen they just left. Deliberately not persisted any further than this navigation: opening the
 * booking again later is a new question, not the same one.
 */
export const ADD_ONS_DECLINED_PARAM = 'addOnsDeclined';

interface BookingAddOnsOffer {
  shouldOffer: boolean;
  bookingId: string | null;
  /* Only wait on this if you are choosing a SCREEN; a caller that just hides itself should not. */
  isDeciding: boolean;
}

/* Bounded: a hung read must not strand a tenant who has paid. Losing the offer is the right loss. */
const DECISION_TIMEOUT_MS = 3000;

/*
 * Whether a completed booking should be offered the add-on step.
 *
 * Decides on the SAME keyed, start-filtered menu the step will show. Reading the shared
 * `state.resource.resource` slot instead looks equivalent and is not: it was populated during
 * checkout, before the booking existed, so its add-ons are unfiltered by minimum notice
 * (ACCESS-2591 / RES-1246). The step re-reads with the booking's start and bounces when that comes
 * back empty — so an unfiltered card offers a step that refuses it. Tap, bounce, tap, bounce.
 *
 * The booking is passed in rather than read from the store: `useReceipt` resolves ITS booking out of
 * the transactions LIST, while the store slot holds the last one booked. Opened from my-bookings
 * those differ, and the card would answer for the wrong booking.
 */
export const useBookingAddOnsOffer = (booking: TransactionDetails | undefined): BookingAddOnsOffer => {
  const dispatch = useDispatch();
  const isKioskMode = useKioskEnabled();
  const adapterName = useCurrentAdapterName();
  const addOnsEnabled = useAddOnsEnabled();
  const { ownerType, ownerUuid } = useOwnerParams();
  const menu = useSelector(selectBookingAddOnsMenu);

  const resourceId = booking?.details?.resource_id;
  const bookingStart = booking?.details?.start_at;

  /* Store-local conditions first, so the room read below is only spent on a booking that could pass. */
  const meetsPrerequisites = useMemo(
    () => bookingAddOnsPrerequisites({ booking, adapterName, addOnsEnabled, isKioskMode }),
    [booking, adapterName, addOnsEnabled, isKioskMode],
  );

  /* Not part of eligibility: an existing order stops the offer, but the amend path still opens the
   * picker on those bookings. Folded into `canReadMenu` too, so they cost no room read. */
  const isFirstTimeOffer = !hasOrderedAddOns(booking);

  const menuKey = resourceId && bookingStart ? bookingAddOnsMenuKey(resourceId, bookingStart) : null;
  const canReadMenu = Boolean(meetsPrerequisites && isFirstTimeOffer && menuKey && ownerType && ownerUuid);

  /* Once per mount, not per session: notice windows close as the booking approaches. */
  const requestedMenuKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (canReadMenu && menuKey && requestedMenuKeyRef.current !== menuKey) {
      requestedMenuKeyRef.current = menuKey;
      dispatch(
        getBookingAddOnsMenu.request({
          key: menuKey,
          params: {
            ownerType,
            ownerUuid,
            resourceUuid: String(resourceId),
            getResourceOptions: { start_at: bookingStart },
          },
        }),
      );
    }
  }, [dispatch, canReadMenu, menuKey, ownerType, ownerUuid, resourceId, bookingStart]);

  /* FULFILLED, not settled: anything else resolves to null and fails closed through one path. */
  const menuResource =
    canReadMenu && menu.key === menuKey && menu.status === ACTION_STATUSES.FULFILLED ? menu.resource : null;

  /* REJECTED is settled — a definite no, not a reason to keep waiting. */
  const menuSettled =
    menu.key === menuKey && (menu.status === ACTION_STATUSES.FULFILLED || menu.status === ACTION_STATUSES.REJECTED);

  /*
   * "Not ready yet" is not "no", and only this hook can tell them apart: the cheap gate answers false
   * both for a real no and while the transaction or app-instance-configs are still landing. A caller
   * sees one boolean, so reporting "settled" early lets it latch a permanent no on first render.
   */
  /*
   * `adapterName` alone proves the configs landed — every config carries a vertical adapter.
   * Deliberately NOT `addOnsEnabled`: that is a wire field, so `undefined` means both "not loaded yet"
   * and "this config omits the key", and a building whose config lacks it would wait the full timeout
   * on every post-checkout receipt. A missing value is false, which the prerequisites already handle.
   */
  const inputsResolved = Boolean(booking) && adapterName !== undefined;

  const [waitedTooLong, setWaitedTooLong] = useState(false);

  /* Either reason, under the one bound. */
  const isDeciding = (!inputsResolved || (Boolean(canReadMenu) && !menuSettled)) && !waitedTooLong;

  useEffect(() => {
    if (!isDeciding) {
      return undefined;
    }

    const timer = setTimeout(() => setWaitedTooLong(true), DECISION_TIMEOUT_MS);

    return () => clearTimeout(timer);
  }, [isDeciding]);

  return useMemo<BookingAddOnsOffer>(() => {
    const { isEligible, bookingId } = bookingAddOnsEligibility({
      booking,
      resource: menuResource,
      adapterName,
      addOnsEnabled,
      isKioskMode,
    });

    return { shouldOffer: isEligible && isFirstTimeOffer, bookingId, isDeciding };
  }, [booking, menuResource, adapterName, addOnsEnabled, isKioskMode, isDeciding, isFirstTimeOffer]);
};
